import { useState, useEffect } from "react";
import axios from "axios";
import Banner from "../../components/Banner";
import Header from "../../components/Header";
import NewsQuoteBanner from "../../components/NewsQuoteBanner";
import Footer from "../../components/Footer";
import CategoryDropdown from "../../components/CategoryDropdown";
import NewsCard from "../../components/NewsCard";
import backgroundImage from "../../img/banner/ZTO-Banner.png";
import newsImage1 from "../../img/default-image.jpg";
import { Link, useSearchParams } from "react-router-dom";
import { IoSearchSharp } from "react-icons/io5";
import { t } from "i18next";
import "../../css/news.css";
import CTASection from "../../components/CTASection";
import { API_URL_All } from "../../api";

const News = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tagQuery = (searchParams.get("tag") || "").trim();
  const isTagMode = Boolean(tagQuery);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [newsData, setNewsData] = useState([]);
  const [filteredNews, setFilteredNews] = useState([]);
  const [displayedNews, setDisplayedNews] = useState([]);
  const [loadMoreCount, setLoadMoreCount] = useState(8);
  const [loading, setLoading] = useState(false);

  // Dropdown options based on Vietnam Post Logistics categories
  const categoryOptions = [
    { value: "tin-tuc-thi-truong", label: "Tin tức thị trường" },
    { value: "kien-thuc-xnk", label: "Kiến thức XNK" },
    { value: "nguon-hang", label: "Nguồn hàng" },
    { value: "tat-ca-tin-tuc", label: "Tất cả tin tức" },
  ];

  useEffect(() => {
    setSearchQuery(tagQuery);
    if (isTagMode) {
      setSelectedCategory(null);
    }
  }, [tagQuery, isTagMode]);

  // Initialize data on component mount and reload when tag search changes
  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL_All}/api/news`, {
          params: isTagMode ? { title: tagQuery } : {},
        });
        const mapped = (res.data || []).map((item) => ({
          ...item,
          image: item.imageUrl || newsImage1,
          date: item.publishDate || item.date || "",
          category: item.categorySlug || item.category || "",
          categoryLabel: item.category || item.categoryLabel || "",
          featured: item.featured || false,
        }));
        setNewsData(mapped);
        setFilteredNews(mapped);
        setLoadMoreCount(8);
      } catch (err) {
        console.error("Error loading news", err);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [isTagMode, tagQuery]);

  useEffect(() => {
    setDisplayedNews(filteredNews.slice(0, loadMoreCount));
  }, [filteredNews, loadMoreCount]);

  const filterNews = (query = searchQuery, category = selectedCategory) => {
    let filtered = newsData;

    // Filter by category
    if (category && category.value !== "tat-ca-tin-tuc") {
      filtered = filtered.filter((news) => news.category === category.value);
    }

    // Filter by search query
    if (query.trim()) {
      if (isTagMode) {
        filtered = filtered.filter(
          (news) => news.title.toLowerCase().includes(query.toLowerCase()),
        );
      } else {
        filtered = filtered.filter(
          (news) =>
            news.title.toLowerCase().includes(query.toLowerCase()) ||
            (news.excerpt || "").toLowerCase().includes(query.toLowerCase()),
        );
      }
    }

    setFilteredNews(filtered);
    setLoadMoreCount(8);
  };

  const handleSearch = () => {
    if (isTagMode) {
      const keyword = searchQuery.trim();
      if (keyword) {
        setSearchParams({ tag: keyword });
      } else {
        setSearchParams({});
      }
      return;
    }

    if (searchQuery.trim()) {
      filterNews(searchQuery, selectedCategory);
    } else {
      // Reset to show all news if search is empty
      filterNews("", selectedCategory);
    }
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const handleCategorySelect = (category) => {
    if (isTagMode) return;
    setSelectedCategory(category);
    filterNews(searchQuery, category);
  };

  const handleLoadMore = () => {
    setLoadMoreCount((prev) => prev + 8);
  };

  // Get featured news (first item with featured=true)
  const getFeaturedNews = () => {
    const featured = filteredNews.find((news) => news.featured === true);
    console.log("Featured News:", featured);
    return featured || (filteredNews.length > 0 ? filteredNews[0] : null);
  };

  // Get secondary news (2nd and 3rd featured items, or next 2 items)
  const getSecondaryNews = () => {
    const featuredNews = filteredNews.filter((news) => news.featured === true);

    if (featuredNews.length >= 3) {
      return featuredNews.slice(1, 3); // index 1 và 2
    }

    if (featuredNews.length === 2) {
      return featuredNews.slice(1, 2); // chỉ index 1
    }

    // length === 1 hoặc 0
    return [];
  };

  // Get grid news for "Tất cả tin tức" section (all news in grid format)
  const getGridNews = () => {
    return displayedNews;
  };

  // Get news by category for individual sections
  const getNewsByCategory = (category, limit = 3) => {
    const categoryNews = filteredNews.filter(
      (news) => news.category === category,
    );
    return categoryNews.slice(0, limit);
  };

  // Get featured news for each category
  const getCategoryFeatured = (category) => {
    const categoryNews = filteredNews.filter(
      (news) => news.category === category,
    );
    return categoryNews.length > 0 ? categoryNews[0] : null;
  };

  // Get secondary news for each category
  const getCategorySecondary = (category) => {
    const categoryNews = filteredNews.filter(
      (news) => news.category === category,
    );
    return categoryNews.slice(1, 3);
  };

  return (
    <div className="news-section">
      <Header />
      <Banner
        title="Tin tức"
        backgroundImage={backgroundImage}
        breadcrumb={
          <>
            <Link to={"/"} className="text-white p-2">
              Trang chủ
            </Link>
            <span>{">"}</span>
            <Link to={"/news"} className="text-white p-2">
              Tin tức
            </Link>
          </>
        }
      />

      <div className="container-fluid pb-5" style={{ background: "#F2F4F7" }}>
        <div className="container pt-4 pt-md-5">
          <div className="search-section row align-items-center justify-content-between">
            <div className={`${isTagMode ? "col-md-8" : "col-md-6"} mb-3 mb-md-0`}>
              <div className="input-group search-box">
                <input
                  type="text"
                  className="form-control text-dark"
                  placeholder="Tìm kiếm tin tức"
                  aria-label="Tìm kiếm tin tức"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                />
                <button
                  className="btn search-btn"
                  type="button"
                  onClick={handleSearch}
                >
                  <IoSearchSharp size={20} />
                </button>
              </div>
            </div>
            {!isTagMode && (
              <div className="col-md-4 mb-3 mb-md-0 d-flex justify-content-center">
                <CategoryDropdown
                  options={categoryOptions}
                  placeholder="Theo chuyên mục"
                  onSelect={handleCategorySelect}
                />
              </div>
            )}
            <div className={`${isTagMode ? "col-md-4" : "col-md-2"} d-flex justify-content-end`}>
              <button
                className="btn search-news-btn"
                onClick={handleSearch}
              >
                TÌM KIẾM TIN TỨC
              </button>
            </div>
          </div>

          <div className="news-lists pt-3 pt-md-5">
            {loading ? (
              <div className="text-center my-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <>
                {!isTagMode && (
                  <>
                    {/* Tin tức nổi bật */}
                    {getFeaturedNews() && (
                      <div className="tin-tuc-noi-bat">
                        <h3>Tin tức nổi bật</h3>
                        <div className="news-content row">
                          <div className="col-md-6 mb-3 mb-md-0">
                            <NewsCard
                              news={getFeaturedNews()}
                              isLarge={true}
                              showExcerpt={true}
                            />
                          </div>

                          <div className="col-md-6">
                            <div className="row h-100">
                              {getSecondaryNews().map((news, index) => (
                                <div
                                  key={news.id}
                                  className={`col-12 ${index === 0 ? "mb-3" : ""}`}
                                >
                                  <NewsCard news={news} isLarge={false} />
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Tin tức thị trường */}
                    {getNewsByCategory("tin-tuc-thi-truong").length > 0 && (
                      <div className="tin-tuc-thi-truong pt-5">
                        <h3>Tin tức thị trường</h3>
                        <div className="news-content row">
                          <div className="col-md-6 mb-3 mb-md-0">
                            {getCategoryFeatured("tin-tuc-thi-truong") && (
                              <NewsCard
                                news={getCategoryFeatured("tin-tuc-thi-truong")}
                                isLarge={true}
                                showExcerpt={true}
                              />
                            )}
                          </div>

                          <div className="col-md-6">
                            <div className="row h-100">
                              {getCategorySecondary("tin-tuc-thi-truong").map(
                                (news, index) => (
                                  <div
                                    key={news.id}
                                    className={`col-12 ${index === 0 ? "mb-3" : ""}`}
                                  >
                                    <NewsCard news={news} isLarge={false} />
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Kiến thức XNK */}
                    {getNewsByCategory("kien-thuc-xnk").length > 0 && (
                      <div className="kien-thuc-xnk pt-5">
                        <h3>Kiến thức XNK</h3>
                        <div className="news-content row">
                          <div className="col-md-6 mb-3 mb-md-0">
                            {getCategoryFeatured("kien-thuc-xnk") && (
                              <NewsCard
                                news={getCategoryFeatured("kien-thuc-xnk")}
                                isLarge={true}
                                showExcerpt={true}
                              />
                            )}
                          </div>

                          <div className="col-md-6">
                            <div className="row h-100">
                              {getCategorySecondary("kien-thuc-xnk").map(
                                (news, index) => (
                                  <div
                                    key={news.id}
                                    className={`col-12 ${index === 0 ? "mb-3" : ""}`}
                                  >
                                    <NewsCard news={news} isLarge={false} />
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Nguồn hàng */}
                    {getNewsByCategory("nguon-hang").length > 0 && (
                      <div className="nguon-hang pt-5">
                        <h3>Nguồn hàng</h3>
                        <div className="news-content row">
                          <div className="col-md-6 mb-3 mb-md-0">
                            {getCategoryFeatured("nguon-hang") && (
                              <NewsCard
                                news={getCategoryFeatured("nguon-hang")}
                                isLarge={true}
                                showExcerpt={true}
                              />
                            )}
                          </div>

                          <div className="col-md-6">
                            <div className="row h-100">
                              {getCategorySecondary("nguon-hang").map(
                                (news, index) => (
                                  <div
                                    key={news.id}
                                    className={`col-12 ${index === 0 ? "mb-3" : ""}`}
                                  >
                                    <NewsCard news={news} isLarge={false} />
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Tất cả tin tức */}
                <div className="tat-ca-tin-tuc pt-5">
                  <h3>{isTagMode ? `Kết quả theo từ khóa: "${tagQuery}"` : "Tất cả tin tức"}</h3>
                  <div className="news-grid row">
                    {getGridNews().map((news) => (
                      <div
                        key={news.id}
                        className="col-12 col-md-6 col-lg-3 mb-4"
                      >
                        <NewsCard
                          news={news}
                          isLarge={false}
                          isNewsAll={true}
                        />
                      </div>
                    ))}
                  </div>

                  {displayedNews.length < filteredNews.length && (
                    <div className="text-center mt-4">
                      <button
                        className="load-more-btn"
                        onClick={handleLoadMore}
                      >
                        Xem thêm
                      </button>
                    </div>
                  )}

                  {filteredNews.length === 0 && (
                    <div className="text-center mt-4">
                      <p className="text-muted">
                        Không tìm thấy tin tức phù hợp.
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* <NewsQuoteBanner /> */}
      <CTASection />
      <Footer />
    </div>
  );
};

export default News;
